import { ShopProductStatus } from '@kanbu/schema/enums';
import { DateFormat, formatUtils } from '@kanbu/shared';
import { Trans, useLingui } from '@lingui/react/macro';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import { Badge, Button, Dialog, Label, Switch } from '@utima/ui';
import { AlertTriangle, ExternalLink, ImageOff, Loader2 } from 'lucide-react';
import { memo, useState } from 'react';

import { Card } from '@/components/card/Card';
import { DisplayValue } from '@/components/displayValue/DisplayValue';
import { useEnumTranslations } from '@/hooks';
import { trpc } from '@/services/trpc';

import { ImageLightbox } from './ImageLightbox';
import { ProductAttributesCard } from './ProductAttributesCard';
import { useShopProductMutation } from '../useShopProductMutation';

export type ProductDetailModalProps = {
  productId: string | null | undefined;
  onClose: () => void;
  /**
   * Only used to link the attributes section at the whole-feed vocabulary
   * screen. `ShopProductType` deliberately omits its `feed` relation, so the
   * ids come from the route rather than the product payload.
   */
  chatId?: string;
  feedId?: string;
};

export const ProductDetailModal = memo(function ProductDetailModal({
  productId,
  onClose,
  chatId,
  feedId,
}: ProductDetailModalProps) {
  const { t } = useLingui();
  const enumTranslations = useEnumTranslations();
  const {
    update: { mutateAsync: update, isPending: isUpdating },
  } = useShopProductMutation();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const { data: product, isLoading } = trpc.shopProducts.findOne.useQuery(
    {
      id: productId!,
      populate: ['categories', 'attributes.attribute'],
    },
    { enabled: !!productId },
  );

  const images = [
    ...(product?.imageUrl ? [product.imageUrl] : []),
    ...(product?.images ?? []),
  ].filter((url, index, all) => all.indexOf(url) === index);

  return (
    <>
      <Dialog.Root open={!!productId} onOpenChange={onClose}>
        <Dialog.Content className='flex h-[90vh] w-full max-w-[900px] flex-col overflow-hidden'>
          <Dialog.Header>
            <Dialog.Title>{product?.name}</Dialog.Title>
          </Dialog.Header>

          {isLoading ? (
            <div className='flex min-h-40 items-center justify-center text-primary'>
              <Loader2 className='size-10 animate-spin' />
            </div>
          ) : (
            <ScrollArea.Root className='min-h-0 flex-1'>
              <ScrollArea.Viewport className='size-full p-4'>
                <div className='flex flex-col gap-4'>
                  {product?.status === ShopProductStatus.Failed &&
                    product.lastError && (
                      <Card
                        variant='danger'
                        title={
                          <span className='flex items-center gap-2 text-danger'>
                            <AlertTriangle className='size-4' />
                            <Trans>Last embedding error</Trans>
                          </span>
                        }
                      >
                        <p className='whitespace-pre-wrap text-sm text-danger'>
                          {product.lastError}
                        </p>
                      </Card>
                    )}

                  {images.length > 0 && (
                    <Card title={t`Images`}>
                      <div className='flex flex-wrap gap-3'>
                        {images.map((url, index) => (
                          <button
                            key={url}
                            type='button'
                            onClick={() => setLightboxIndex(index)}
                            aria-label={t`Expand image`}
                          >
                            <img
                              src={url}
                              alt={product?.name}
                              className='size-24 cursor-zoom-in rounded-md border border-border object-cover transition-opacity hover:opacity-80'
                            />
                          </button>
                        ))}
                      </div>
                    </Card>
                  )}

                  {images.length === 0 && (
                    <Card title={t`Images`}>
                      <div className='flex items-center gap-2 text-sm text-muted-fg'>
                        <ImageOff className='size-4' />
                        <Trans>No images available</Trans>
                      </div>
                    </Card>
                  )}

                  <Card title={t`Details`}>
                    <div className='grid grid-cols-2 gap-4 md:grid-cols-3'>
                      <DisplayValue label={t`Price`} size='sm'>
                        {product &&
                          (product.price == null
                            ? t`Not available`
                            : formatUtils.currency(
                                Number(product.price),
                                product.currency,
                              ))}
                      </DisplayValue>
                      <DisplayValue label={t`Availability`} size='sm'>
                        {product && (
                          <Badge size='sm' outline>
                            {
                              enumTranslations.productAvailabilityStatus[
                                product.availability
                              ]
                            }
                          </Badge>
                        )}
                      </DisplayValue>
                      <DisplayValue label={t`Embedding status`} size='sm'>
                        {product && (
                          <Badge size='sm' outline>
                            {enumTranslations.shopProductStatus[product.status]}
                          </Badge>
                        )}
                      </DisplayValue>
                      <DisplayValue label={t`Brand`} size='sm'>
                        {product?.brand ?? '-'}
                      </DisplayValue>
                      <DisplayValue label={t`Type`} size='sm'>
                        {product?.type ?? '-'}
                      </DisplayValue>
                      <DisplayValue label={t`EAN`} size='sm'>
                        {product?.ean ?? '-'}
                      </DisplayValue>
                      <DisplayValue label={t`ISBN`} size='sm'>
                        {product?.isbn ?? '-'}
                      </DisplayValue>
                      <DisplayValue label={t`External ID`} size='sm'>
                        {product?.externalId ?? '-'}
                      </DisplayValue>
                      <DisplayValue
                        label={t`Dispatch lead time (days)`}
                        size='sm'
                      >
                        {product?.dispatchLeadTimeDays ?? '-'}
                      </DisplayValue>
                      <DisplayValue
                        label={t`Availability / launch date`}
                        size='sm'
                      >
                        {product?.availabilityDate
                          ? new Date(product.availabilityDate).toLocaleString()
                          : '-'}
                      </DisplayValue>
                      <DisplayValue label={t`Estimated arrival date`} size='sm'>
                        {product?.deliveryDate
                          ? new Date(product.deliveryDate).toLocaleString()
                          : '-'}
                      </DisplayValue>
                      <DisplayValue label={t`Stock quantity`} size='sm'>
                        {product?.stockQuantity ?? '-'}
                      </DisplayValue>
                    </div>

                    {product?.url && (
                      <a
                        href={product.url}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='inline-flex w-fit items-center gap-1 text-sm text-primary underline'
                      >
                        {t`Open source URL`}
                        <ExternalLink className='size-3.5' />
                      </a>
                    )}

                    <div className='flex items-center gap-2 pt-2'>
                      <Switch
                        id='product-active-switch'
                        checked={!!product?.active}
                        disabled={!product || isUpdating}
                        onCheckedChange={checked =>
                          product && update({ id: product.id, active: checked })
                        }
                      />
                      <Label htmlFor='product-active-switch'>
                        <Trans>Active (served to the assistant)</Trans>
                      </Label>
                    </div>
                  </Card>

                  {product?.shortSummary && (
                    <Card title={t`Model summary (short)`}>
                      <p className='whitespace-pre-wrap text-sm'>
                        {product.shortSummary}
                      </p>
                    </Card>
                  )}

                  {product?.description && (
                    <Card title={t`Description`}>
                      <p className='whitespace-pre-wrap text-sm'>
                        {product.description}
                      </p>
                    </Card>
                  )}

                  {!!product?.categories?.length && (
                    <Card title={t`Categories`}>
                      <div className='flex flex-wrap gap-2'>
                        {product.categories.map(category => (
                          <Badge key={category.id} size='sm' outline>
                            {category.displayName ?? category.name}
                          </Badge>
                        ))}
                      </div>
                    </Card>
                  )}

                  {product && (
                    <ProductAttributesCard
                      productId={product.id}
                      attributes={product.attributes ?? []}
                      chatId={chatId}
                      feedId={feedId}
                    />
                  )}

                  <Card title={t`Change history`}>
                    <div className='grid grid-cols-2 gap-4 md:grid-cols-4'>
                      <DisplayValue label={t`Last seen in feed`} size='sm'>
                        {product?.lastSeenAt
                          ? formatUtils.date(
                              product.lastSeenAt,
                              DateFormat.DateTimeNumericPrecise,
                            )
                          : '-'}
                      </DisplayValue>
                      <DisplayValue label={t`Price or stock changed`} size='sm'>
                        {product?.offerUpdatedAt
                          ? formatUtils.date(
                              product.offerUpdatedAt,
                              DateFormat.DateTimeNumericPrecise,
                            )
                          : '-'}
                      </DisplayValue>
                      <DisplayValue label={t`Content changed`} size='sm'>
                        {product?.contentUpdatedAt
                          ? formatUtils.date(
                              product.contentUpdatedAt,
                              DateFormat.DateTimeNumericPrecise,
                            )
                          : '-'}
                      </DisplayValue>
                      <DisplayValue label={t`Last enriched`} size='sm'>
                        {product?.enrichedAt
                          ? formatUtils.date(
                              product.enrichedAt,
                              DateFormat.DateTimeNumericPrecise,
                            )
                          : '-'}
                      </DisplayValue>
                    </div>
                  </Card>

                  <Card title={t`Embedding timeline`}>
                    <div className='grid grid-cols-2 gap-4 md:grid-cols-3'>
                      <DisplayValue label={t`Scheduled at`} size='sm'>
                        {formatUtils.date(
                          product?.scheduledAt,
                          DateFormat.DateTimeNumericPrecise,
                        )}
                      </DisplayValue>
                      <DisplayValue label={t`Started embedding at`} size='sm'>
                        {product?.startedEmbeddingAt
                          ? formatUtils.date(
                              product.startedEmbeddingAt,
                              DateFormat.DateTimeNumericPrecise,
                            )
                          : '-'}
                      </DisplayValue>
                      <DisplayValue label={t`Finished embedding at`} size='sm'>
                        {product?.finishedEmbeddingAt
                          ? formatUtils.date(
                              product.finishedEmbeddingAt,
                              DateFormat.DateTimeNumericPrecise,
                            )
                          : '-'}
                      </DisplayValue>
                    </div>
                  </Card>
                </div>
              </ScrollArea.Viewport>
              <ScrollArea.Scrollbar
                className='flex touch-none select-none bg-gray-300 p-1 transition-colors hover:bg-gray-500'
                orientation='vertical'
              >
                <ScrollArea.Thumb className='relative flex-1 rounded-full bg-gray-400' />
              </ScrollArea.Scrollbar>
            </ScrollArea.Root>
          )}

          <Dialog.Footer>
            <Dialog.Close asChild>
              <Button
                data-cta='admin-product-detail-modal-close-button'
                variant='secondary'
              >
                <Trans>Close</Trans>
              </Button>
            </Dialog.Close>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Root>

      {lightboxIndex !== null && (
        <ImageLightbox
          images={images}
          annotations={images.map(
            url => product?.imageCaptionByUrl?.[url] ?? '',
          )}
          index={lightboxIndex}
          alt={product?.name}
          onIndexChange={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  );
});
